import { ExportUtils } from './export-utils'
import { format } from 'date-fns'

export interface Shareholder {
  id: string
  name: string
  email?: string
  type: 'founder' | 'employee' | 'investor' | 'advisor'
}

export interface EquityClass {
  id: string
  class_name: string
  class_type: 'common' | 'preferred' | 'options' | 'warrants'
  liquidation_preference?: number | null
  participation_cap_multiple?: number | null
  conversion_ratio?: number | null
  seniority_rank?: number | null
}

export interface CapTableEntry {
  user_id: string
  shareholder?: Shareholder
  equity_class: EquityClass
  shares: number
  percentage: number
  vested_shares?: number
  unvested_shares?: number
  strike_price?: number
  grant_date?: string
  vesting_start_date?: string
  vesting_schedule?: string
}

export interface FundingRound {
  id: string
  round_name: string
  round_date: string
  pre_money_valuation: number | null
  post_money_valuation: number | null
  target_amount: number | null
  [key: string]: any // Allow additional DB fields
}

export interface ValuationData {
  valuation_date: string
  post_money_valuation: number | null
  common_share_price: number
  total_shares_outstanding: number | null
  [key: string]: any // Allow additional DB fields
}

export interface CapTableExportData {
  companyName: string
  exportDate: Date
  capTable: CapTableEntry[]
  equityClasses: EquityClass[]
  fundingRounds: FundingRound[]
  valuations: ValuationData[]
  authorizedShares: {
    common: number
    preferred: number
    total: number
  }
  issuedShares: {
    common: number
    preferred: number
    options: number
    warrants: number
    total: number
  }
}

export class CapTableFormatter {
  /**
   * Format cap table data for export
   */
  static formatCapTableData(data: CapTableExportData) {
    const { capTable, equityClasses } = data

    // Group by shareholder
    const shareholderMap = new Map<string, {
      shareholder: Shareholder
      holdings: CapTableEntry[]
      totalShares: number
      totalPercentage: number
    }>()

    capTable.forEach(entry => {
      if (!entry.shareholder) return

      const key = entry.user_id
      if (!shareholderMap.has(key)) {
        shareholderMap.set(key, {
          shareholder: entry.shareholder,
          holdings: [],
          totalShares: 0,
          totalPercentage: 0
        })
      }

      const shareholderData = shareholderMap.get(key)!
      shareholderData.holdings.push(entry)
      shareholderData.totalShares += entry.shares
      shareholderData.totalPercentage += entry.percentage
    })

    return Array.from(shareholderMap.values()).sort((a, b) => 
      b.totalPercentage - a.totalPercentage
    )
  }

  /**
   * Calculate fully diluted shares
   */
  static calculateFullyDiluted(data: CapTableExportData) {
    const { capTable, authorizedShares } = data

    const issuedShares = capTable.reduce((sum, entry) => sum + entry.shares, 0)
    const reservedShares = authorizedShares.total - issuedShares

    return {
      issuedShares,
      reservedShares,
      fullyDilutedShares: authorizedShares.total,
      utilizationRate: issuedShares / authorizedShares.total
    }
  }

  /**
   * Generate summary statistics
   */
  static generateSummaryStats(data: CapTableExportData) {
    const shareholdersByType = new Map<string, {
      count: number
      shares: number
      percentage: number
    }>()

    data.capTable.forEach(entry => {
      const type = entry.shareholder?.type || 'unknown'
      if (!shareholdersByType.has(type)) {
        shareholdersByType.set(type, { count: 0, shares: 0, percentage: 0 })
      }
      
      const stats = shareholdersByType.get(type)!
      stats.count++
      stats.shares += entry.shares
      stats.percentage += entry.percentage
    })

    return Array.from(shareholdersByType.entries()).map(([type, stats]) => ({
      type,
      ...stats
    }))
  }

  /**
   * Export to Excel with multiple sheets
   */
  static async exportToExcel(data: CapTableExportData) {
    const formattedHolders = this.formatCapTableData(data)
    const fullyDiluted = this.calculateFullyDiluted(data)
    const summaryStats = this.generateSummaryStats(data)

    const sheets = [
      // Summary sheet
      {
        name: 'Summary',
        data: [
          { metric: 'Company Name', value: data.companyName },
          { metric: 'Export Date', value: format(data.exportDate, 'MMMM d, yyyy') },
          { metric: 'Total Authorized Shares', value: ExportUtils.formatNumber(data.authorizedShares.total) },
          { metric: 'Total Issued Shares', value: ExportUtils.formatNumber(data.issuedShares.total) },
          { metric: 'Fully Diluted Shares', value: ExportUtils.formatNumber(fullyDiluted.fullyDilutedShares) },
          { metric: 'Utilization Rate', value: ExportUtils.formatPercentage(fullyDiluted.utilizationRate) },
          { metric: '', value: '' }, // Blank row
          { metric: 'Common Shares Authorized', value: ExportUtils.formatNumber(data.authorizedShares.common) },
          { metric: 'Common Shares Issued', value: ExportUtils.formatNumber(data.issuedShares.common) },
          { metric: 'Preferred Shares Authorized', value: ExportUtils.formatNumber(data.authorizedShares.preferred) },
          { metric: 'Preferred Shares Issued', value: ExportUtils.formatNumber(data.issuedShares.preferred) },
          { metric: 'Options Issued', value: ExportUtils.formatNumber(data.issuedShares.options) },
          { metric: 'Warrants Issued', value: ExportUtils.formatNumber(data.issuedShares.warrants) }
        ],
        columns: [
          { header: 'Metric', key: 'metric', width: 30 },
          { header: 'Value', key: 'value', width: 20 }
        ]
      },

      // Cap table sheet
      {
        name: 'Cap Table',
        data: formattedHolders.flatMap(holder => 
          holder.holdings.map(holding => ({
            name: holder.shareholder.name,
            type: holder.shareholder.type,
            email: holder.shareholder.email || '',
            equity_class: holding.equity_class.class_name,
            shares: holding.shares,
            percentage: ExportUtils.formatPercentage(holding.percentage / 100),
            vested: holding.vested_shares || 0,
            unvested: holding.unvested_shares || 0,
            strike_price: holding.strike_price ? ExportUtils.formatCurrency(holding.strike_price) : '',
            grant_date: holding.grant_date ? format(new Date(holding.grant_date), 'MM/dd/yyyy') : ''
          }))
        ),
        columns: [
          { header: 'Name', key: 'name', width: 25 },
          { header: 'Type', key: 'type', width: 15 },
          { header: 'Email', key: 'email', width: 30 },
          { header: 'Equity Class', key: 'equity_class', width: 20 },
          { header: 'Shares', key: 'shares', width: 15 },
          { header: 'Ownership %', key: 'percentage', width: 15 },
          { header: 'Vested', key: 'vested', width: 15 },
          { header: 'Unvested', key: 'unvested', width: 15 },
          { header: 'Strike Price', key: 'strike_price', width: 15 },
          { header: 'Grant Date', key: 'grant_date', width: 15 }
        ]
      },

      // Ownership by type
      {
        name: 'Ownership by Type',
        data: summaryStats.map(stat => ({
          type: stat.type.charAt(0).toUpperCase() + stat.type.slice(1),
          holders: stat.count,
          shares: ExportUtils.formatNumber(stat.shares),
          percentage: ExportUtils.formatPercentage(stat.percentage / 100)
        })),
        columns: [
          { header: 'Type', key: 'type', width: 20 },
          { header: 'Number of Holders', key: 'holders', width: 20 },
          { header: 'Total Shares', key: 'shares', width: 20 },
          { header: 'Ownership %', key: 'percentage', width: 20 }
        ]
      },

      // Funding history
      {
        name: 'Funding History',
        data: data.fundingRounds.map(round => ({
          round_name: round.round_name,
          round_date: format(new Date(round.round_date), 'MM/dd/yyyy'),
          pre_money: ExportUtils.formatCurrency(round.pre_money_valuation || 0),
          target_amount: ExportUtils.formatCurrency(round.target_amount || 0),
          post_money: ExportUtils.formatCurrency(round.post_money_valuation || 0),
        })),
        columns: [
          { header: 'Round', key: 'round_name', width: 15 },
          { header: 'Date', key: 'round_date', width: 15 },
          { header: 'Pre-Money', key: 'pre_money', width: 20 },
          { header: 'Target Amount', key: 'target_amount', width: 20 },
          { header: 'Post-Money', key: 'post_money', width: 20 },
        ]
      },

      // Equity classes
      {
        name: 'Equity Classes',
        data: data.equityClasses.map(ec => ({
          class_name: ec.class_name,
          class_type: ec.class_type,
          liquidation_pref: ec.liquidation_preference || 1,
          participation_cap: ec.participation_cap_multiple || 'None',
          conversion_ratio: ec.conversion_ratio || 1,
          seniority_rank: ec.seniority_rank || 'N/A'
        })),
        columns: [
          { header: 'Class Name', key: 'class_name', width: 25 },
          { header: 'Type', key: 'class_type', width: 15 },
          { header: 'Liquidation Pref', key: 'liquidation_pref', width: 20 },
          { header: 'Participation Cap', key: 'participation_cap', width: 20 },
          { header: 'Conversion Ratio', key: 'conversion_ratio', width: 20 },
          { header: 'Seniority Rank', key: 'seniority_rank', width: 15 }
        ]
      }
    ]

    await ExportUtils.exportToExcel(sheets, {
      filename: `${data.companyName.replace(/\s+/g, '_')}_cap_table`,
      includeMetadata: true,
      includeTimestamp: true
    })
  }

  /**
   * Export to PDF for investors
   */
  static exportToPDF(data: CapTableExportData) {
    const formattedHolders = this.formatCapTableData(data)
    const fullyDiluted = this.calculateFullyDiluted(data)
    const summaryStats = this.generateSummaryStats(data)

    const sections = [
      // Executive summary
      {
        title: 'Executive Summary',
        summary: `As of ${format(data.exportDate, 'MMMM d, yyyy')}, ${data.companyName} has ${ExportUtils.formatNumber(data.issuedShares.total)} shares issued out of ${ExportUtils.formatNumber(data.authorizedShares.total)} authorized shares.`,
        data: [
          {
            metric: 'Total Authorized Shares',
            value: ExportUtils.formatNumber(data.authorizedShares.total)
          },
          {
            metric: 'Total Issued Shares',
            value: ExportUtils.formatNumber(data.issuedShares.total)
          },
          {
            metric: 'Fully Diluted Shares',
            value: ExportUtils.formatNumber(fullyDiluted.fullyDilutedShares)
          },
          {
            metric: 'Utilization Rate',
            value: ExportUtils.formatPercentage(fullyDiluted.utilizationRate)
          }
        ],
        columns: [
          { header: 'Metric', key: 'metric' },
          { header: 'Value', key: 'value' }
        ]
      },

      // Ownership summary
      {
        title: 'Ownership Summary',
        data: summaryStats.map(stat => ({
          type: stat.type.charAt(0).toUpperCase() + stat.type.slice(1),
          holders: stat.count,
          percentage: ExportUtils.formatPercentage(stat.percentage / 100)
        })),
        columns: [
          { header: 'Type', key: 'type' },
          { header: 'Holders', key: 'holders' },
          { header: 'Ownership %', key: 'percentage' }
        ]
      },

      // Major shareholders (>5%)
      {
        title: 'Major Shareholders (>5%)',
        data: formattedHolders
          .filter(holder => holder.totalPercentage > 5)
          .map(holder => ({
            name: holder.shareholder.name,
            type: holder.shareholder.type,
            shares: ExportUtils.formatNumber(holder.totalShares),
            percentage: ExportUtils.formatPercentage(holder.totalPercentage / 100)
          })),
        columns: [
          { header: 'Name', key: 'name' },
          { header: 'Type', key: 'type' },
          { header: 'Shares', key: 'shares' },
          { header: 'Ownership %', key: 'percentage' }
        ]
      }
    ]

    ExportUtils.exportToPDF(
      `${data.companyName} Cap Table Report`,
      sections,
      {
        filename: `${data.companyName.replace(/\s+/g, '_')}_cap_table_report`,
        includeMetadata: true,
        includeTimestamp: true
      }
    )
  }

  /**
   * Generate 409A report format
   */
  static generate409AFormat(data: CapTableExportData) {
    const latestValuation = data.valuations[0]
    const formattedHolders = this.formatCapTableData(data)

    return {
      // Company information
      company: {
        name: data.companyName,
        valuationDate: latestValuation?.valuation_date,
        pricePerShare: latestValuation?.price_per_share
      },

      // Share structure
      shareStructure: {
        authorized: data.authorizedShares,
        issued: data.issuedShares,
        reserved: {
          optionPool: data.authorizedShares.total - data.issuedShares.total,
          unissuedOptions: 0 // Calculate from cap table
        }
      },

      // Shareholder summary
      shareholderSummary: formattedHolders.map(holder => ({
        name: holder.shareholder.name,
        type: holder.shareholder.type,
        commonShares: holder.holdings
          .filter(h => h.equity_class.class_type === 'common')
          .reduce((sum, h) => sum + h.shares, 0),
        preferredShares: holder.holdings
          .filter(h => h.equity_class.class_type === 'preferred')
          .reduce((sum, h) => sum + h.shares, 0),
        options: holder.holdings
          .filter(h => h.equity_class.class_type === 'options')
          .reduce((sum, h) => sum + h.shares, 0),
        totalShares: holder.totalShares,
        fullyDilutedOwnership: holder.totalPercentage / 100
      })),

      // Option grants
      optionGrants: data.capTable
        .filter(entry => entry.equity_class.class_type === 'options')
        .map(entry => ({
          holder: entry.shareholder?.name || 'Unknown',
          grantDate: entry.grant_date,
          shares: entry.shares,
          strikePrice: entry.strike_price,
          vestedShares: entry.vested_shares || 0,
          unvestedShares: entry.unvested_shares || 0,
          vestingSchedule: entry.vesting_schedule || '4-year with 1-year cliff'
        })),

      // Recent transactions
      recentTransactions: data.fundingRounds.map(round => ({
        date: round.round_date,
        description: `${round.round_name} - ${round.round_type}`,
        sharesIssued: round.shares_issued,
        pricePerShare: round.share_price,
        totalConsideration: round.amount_raised
      }))
    }
  }
}